import { useEffect, useState } from "react";
import styled from "styled-components";
import { ReactComponent as FilesSvg } from "../../../assets/images/icons/files.svg";
import { ReactComponent as InfoSvg } from "../../../assets/images/icons/info.svg";
import { ReactComponent as RefreshSvg } from "../../../assets/images/icons/refresh.svg";
import AdminActionsButton from "../../../components/button/AdminActionsButton";
import SearchInputBar from "../../../components/input/SearchInputBar";
import AdminSidebar from "../../../components/sidebar/AdminSidebar";
import H3 from "../../../components/typography/H3";
import colors from "../../../theme/color/ColorTheme";
import fontSize from "../../../theme/fonts/FontTheme";
import OrderTable from "./components/OrderTable";

import LoadingAnimation from "../../../components/animation/LoadingAnimation";
import { orderService } from "../../../service/OrderService";
const Wrapper = styled.div`
  display: flex;
  width: 100%;
  min-height: 100vh;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const Nav = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  z-index: 10;

  @media (max-width: 1024px) {
    position: relative;
    height: auto;
    width: 100%;
  }
`;

const Content = styled.div`
  flex: 1;
  background-color: ${colors.backgroundTintPrimary};
  padding: 20px;
  margin-left: 260px;
  width: calc(100% - 260px);
  @media (max-width: 1200px) {
    margin-left: 80px;
    width: calc(100% - 80px);
    padding: 15px;
  }

  @media (max-width: 1024px) {
    margin-left: 0;
    width: 100%;
    padding: 10px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 24px;
  border-radius: 12px;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 20px;
  @media (max-width: 768px) {
    padding: 20px 15px;
    border-radius: 8px;
  }
`;
const SortContainer = styled.div`
  padding: 24px;
  border: 1px solid ${colors.dash};
  background-color: ${colors.backgroundTintSecondary};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
`;

const InfoTextSpan = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${fontSize.xs};
`;
const FixedInputWrapper = styled.div`
  width: 20%;
  & > div {
    width: 100%;
  }
  input,
  select {
    width: 100%;
    box-sizing: border-box;
  }
`;

const FilterContainer = styled.div`
  padding: 6px 24px;
  border: 1px solid ${colors.dash};
  background-color: ${colors.backgroundTintSecondary};
  border-radius: 8px;
  align-items: center;
  width: fit-content;
  display: flex;
`;
const FilterButton = styled.button`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  border: ${(props) =>
    props.active ? `1.5px solid ${colors.dash}` : "1px solid transparent"};
  background: ${(props) =>
    props.active ? colors.backgroundPrimary : colors.backgroundTintSecondary};
  color: ${(props) => (props.active ? colors.textPrimary : "#818181")};
  transition: all 0.2s ease;
  &:hover {
    background: ${(props) =>
      props.active ? colors.backgroundPrimary : colors.backgroundTintPrimary};
  }
`;
function OrdersPage() {
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchTerm, setSearchTerm] = useState();
  const [selectedStatus, setSelectedStatus] = useState("New");
  const [orders, setOrders] = useState();
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);
  const [pageType, setPageType] = useState("default");
  const options = ["All", "New", "Ongoing", "Shipped", "Complete"];

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const handleSearch = () => {
    if (searchTerm || selectedCategory !== "") {
      setPageType("search");
      setReload(true);
      setSelectedStatus("All");
      setPage(1);
    }
    return;
  };

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setLoading(true);
    setReload(false);
    const fetchOrders = async () => {
      orderService.getAllOrders(
        page,
        (orders) => {
          setOrders(orders.orders);
          setLoading(false);
        },
        {
          default: (message) => {
            setOrders([]);
            alert(message);
            setLoading(false);
          },
        }
      );
    };
    const fetchOrdersByStatus = async () => {
      orderService.getAllOrdersByStatus(
        selectedStatus,
        page,
        (orders) => {
          setOrders(orders.orders);
          setLoading(false);
        },
        {
          default: (message) => {
            setOrders([]);
            alert(message);
            setLoading(false);
          },
        }
      );
    };
    const fetchOrderBySearch = async () => {
      orderService.searchOrder(
        searchTerm,
        page,
        (orders) => {
          setOrders(orders);
          setLoading(false);
        },
        {
          default: (message) => {
            setOrders([]);
            alert(message);
            setLoading(false);
          },
        }
      );
    };
    if (pageType !== "search") {
      if (selectedStatus === "All") {
        setPageType("default");
        fetchOrders();
      } else {
        setPageType("default");
        fetchOrdersByStatus();
      }
    } else {
      fetchOrderBySearch();
    }
  }, [page, selectedStatus, reload, pageType]);

  return (
    <Wrapper>
      <Nav>
        <AdminSidebar collapsed={window.innerWidth < 1200} />
      </Nav>
      <Content>
        <Container>
          <H3>Orders</H3>

          <SortContainer>
            <InfoTextSpan>
              <InfoSvg />
              <p>
                Manage phone orders, email invoices, and gather payments with
                the help of draft orders.
              </p>
            </InfoTextSpan>
            <ControlsRow>
              {/* <FixedInputWrapper>
                <InputfieldWithDropDown
                  type="dropdown"
                  value={selectedCategory}
                  onChange={(val) => setSelectedCategory(val)}
                  options={["Paid", "Unpaid", "Accessories"]}
                  placeholder="Status"
                />
              </FixedInputWrapper> */}
              <SearchInputBar
                value={searchTerm}
                onChange={(val) => {
                  setSearchTerm(val);
                }}
              />
              {/* <FixedInputWrapper>
                <InputfieldWithDropDown
                  type="dropdown"
                  value={selectedCategory}
                  onChange={(val) => setSelectedCategory(val)}
                  options={[
                    "Clothing",
                    "Shoes",
                    "Accessories",
                    "Electronics",
                    "Home",
                  ]}
                  placeholder="Category"
                />
              </FixedInputWrapper> */}
              <AdminActionsButton
                onClick={() => {
                  handleSearch();
                }}
              >
                <FilesSvg />
                Search
              </AdminActionsButton>
            </ControlsRow>
          </SortContainer>
          <FilterContainer>
            {pageType === "default" &&
              options.map((option) => (
                <FilterButton
                  key={option}
                  active={selectedStatus === option}
                  onClick={() => {
                    setSelectedStatus(option);
                    setPageType("default");
                    setSearchTerm();
                  }}
                >
                  {option}
                </FilterButton>
              ))}
            {pageType === "search" && (
              <FilterButton
                onClick={() => {
                  setSelectedStatus("New");
                  setPageType("default");
                  setSearchTerm("");
                }}
              >
                Cancel Search
              </FilterButton>
            )}
            <FilterButton
              onClick={() => {
                setReload(true);
              }}
            >
              <RefreshSvg height={"30px"} />
            </FilterButton>
          </FilterContainer>
          {loading && <LoadingAnimation height={"500px"} />}
          {orders && (
            <OrderTable orders={orders} updatePage={setPage} page={page} />
          )}
        </Container>
      </Content>
    </Wrapper>
  );
}

export default OrdersPage;
